import {
  FC,
  PropsWithChildren,
  MouseEvent as ReactMouseEvent,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import { isPortalOriginEvent } from '../../utils/portalEvents'

type DesktopContextMenuContextValue = {
  closeAllContextMenus: () => void
  registerCloseContextMenu: (closeContextMenu: () => void) => () => void
}

const DesktopContextMenuContext =
  createContext<DesktopContextMenuContextValue | null>(null)

const noop = () => undefined

/**
 * Coordinates every context menu rendered on the desktop surface. Menus keep
 * their own open state, but register one shared close path here so opening or
 * dismissing a menu cannot leave a sibling menu behind.
 */
export const DesktopContextMenuProvider: FC<PropsWithChildren> = ({
  children
}) => {
  const closeContextMenuCallbacksRef = useRef<Set<() => void>>(new Set())

  const closeAllContextMenus = useCallback(() => {
    closeContextMenuCallbacksRef.current.forEach(closeContextMenu =>
      closeContextMenu()
    )
  }, [])

  const registerCloseContextMenu = useCallback(
    (closeContextMenu: () => void) => {
      closeContextMenuCallbacksRef.current.add(closeContextMenu)
      return () => {
        closeContextMenuCallbacksRef.current.delete(closeContextMenu)
      }
    },
    []
  )

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeAllContextMenus()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [closeAllContextMenus])

  const value = useMemo(
    () => ({ closeAllContextMenus, registerCloseContextMenu }),
    [closeAllContextMenus, registerCloseContextMenu]
  )

  return (
    <DesktopContextMenuContext.Provider value={value}>
      {children}
    </DesktopContextMenuContext.Provider>
  )
}

/**
 * Controlled state for a menu that participates in the desktop menu group.
 * Opening one menu first closes every registered menu, including any menu
 * whose trigger stops DOM event propagation.
 */
export const useDesktopContextMenuState = () => {
  const context = useContext(DesktopContextMenuContext)
  const [isContextMenuOpen, setIsContextMenuOpenState] = useState(false)

  const closeContextMenu = useCallback(() => {
    setIsContextMenuOpenState(false)
  }, [])

  useEffect(() => {
    return context?.registerCloseContextMenu(closeContextMenu)
  }, [context, closeContextMenu])

  const setIsContextMenuOpen = useCallback(
    (isOpen: boolean) => {
      if (isOpen) context?.closeAllContextMenus()
      setIsContextMenuOpenState(isOpen)
    },
    [context]
  )

  return {
    isContextMenuOpen,
    setIsContextMenuOpen,
    closeContextMenu
  }
}

const useCloseDesktopContextMenus = () =>
  useContext(DesktopContextMenuContext)?.closeAllContextMenus ?? noop

/**
 * Dismisses menus for primary clicks on the physical desktop surface. The DOM
 * containment check is important because React portal events still traverse
 * their logical ancestors; a click inside a portalled menu must not dismiss it
 * before its option or submenu handles the event.
 */
export const useDismissDesktopContextMenus = () => {
  const closeAllContextMenus = useCloseDesktopContextMenus()

  return useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      const target = event.target
      if (!(target instanceof Node) || isPortalOriginEvent(event)) return
      if (
        target instanceof Element &&
        target.closest('[data-popover="popover-no-close"]')
      ) {
        return
      }
      closeAllContextMenus()
    },
    [closeAllContextMenus]
  )
}
